/**
 * useStableZoom
 *
 * Shared "stable zoom" plumbing for the uncontrolled map components. Geometry
 * renderers re-style on zoom buckets (0.5 increments via {@link roundZoom}), not
 * on every fractional zoom frame — so this hook keeps a ref-gated rounded zoom
 * and only triggers a re-render when the bucket actually changes.
 *
 * Consumers call `updateStableZoom(rawZoom)` from their map `onMove` handler;
 * the returned `stableZoom` is passed down to geometry overlays.
 *
 * @module components/map/hooks/useStableZoom
 */
import { useCallback, useRef, useState } from 'react';

import { roundZoom } from '@/utils/zoomHelpers';

export interface StableZoomController {
  /** Rounded zoom (0.5 buckets) for geometry renderers. */
  stableZoom: number;
  /** Feed the raw map zoom; re-renders only when the rounded bucket changes. */
  updateStableZoom: (rawZoom: number) => void;
}

/**
 * @param initialZoom Raw zoom the map mounts at; seeds the initial bucket.
 */
export function useStableZoom(initialZoom: number): StableZoomController {
  const stableZoomRef = useRef(roundZoom(initialZoom));
  const [stableZoom, setStableZoom] = useState(() => roundZoom(initialZoom));

  const updateStableZoom = useCallback((rawZoom: number) => {
    const rounded = roundZoom(rawZoom);
    if (rounded !== stableZoomRef.current) {
      stableZoomRef.current = rounded;
      setStableZoom(rounded);
    }
  }, []);

  return { stableZoom, updateStableZoom };
}
